import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  Flight,
  Hotel,
  DirectionsCar,
  Person,
  LocationOn,
  AccessTime,
  Logout,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { createRequest } from '../services/dataServiceWrapper';
import { sendApproverNotification, sendSubmitterConfirmation } from '../services/emailService';
import { notifyApprovalNeeded } from '../services/notificationService';
import { getGraphClient } from '../services/graphService';

// Validation schema
const schema = yup.object({
  submitterName: yup.string().required('Submitter name is required'),
  approver: yup.string().email('Invalid email').required('Approver is required'),
  destinationCity: yup.string().required('Destination city is required'),
  arrivalTime: yup.date().required('Arrival time is required').typeError('Please select a valid date and time'),
  departureTime: yup.date()
    .required('Departure time is required')
    .typeError('Please select a valid date and time')
    .test('after-arrival', 'Departure must be after arrival', function(value) {
      const { arrivalTime } = this.parent;
      return !arrivalTime || !value || value > arrivalTime;
    }),
  accommodationSuburb: yup.string().when('accommodationRequired', {
    is: true,
    then: (schema) => schema.required('Accommodation suburb is required when accommodation is needed'),
    otherwise: (schema) => schema.nullable(),
  }),
  purposeOfTravel: yup.string().required('Purpose of travel is required'),
  additionalRequirements: yup.string(),
});

// TR_Approvers group ID
const TR_APPROVERS_GROUP_ID = '3aaee08d-789a-4f8a-b783-e9bb4528f674';

const TravelRequest = () => {
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const [submitting, setSubmitting] = useState(false);
  const [travellers, setTravellers] = useState([{ id: 1, name: '', departureCity: '' }]);
  const [approvers, setApprovers] = useState([]);
  const [loadingApprovers, setLoadingApprovers] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      submitterName: account?.name || '',
      approver: '',
      destinationCity: '',
      arrivalTime: null,
      departureTime: null,
      purposeOfTravel: '',
      accommodationRequired: false,
      accommodationSuburb: '',
      carHireRequired: false,
      additionalRequirements: '',
    },
  });

  const accommodationRequired = watch('accommodationRequired');

  useEffect(() => {
    loadApprovers();
  }, [account]);

  const loadApprovers = async () => {
    try {
      const graphClient = getGraphClient(instance, account);

      // Get members of TR_Approvers group
      const response = await graphClient
        .api(`/groups/${TR_APPROVERS_GROUP_ID}/members`)
        .select('id,displayName,mail,userPrincipalName')
        .top(999)
        .get();

      const approverList = response.value
        .filter(member => member['@odata.type'] === '#microsoft.graph.user')
        .map(member => ({
          id: member.id,
          name: member.displayName || member.userPrincipalName,
          email: member.mail || member.userPrincipalName,
        }));

      setApprovers(approverList);
      console.log('Loaded TR_Approvers for travel request:', approverList);
    } catch (error) {
      console.error('Error loading approvers:', error);
      toast.error('Failed to load approvers from Azure AD');
    } finally {
      setLoadingApprovers(false);
    }
  };

  const addTraveller = () => {
    setTravellers([...travellers, { id: Date.now(), name: '', departureCity: '' }]);
  };

  const removeTraveller = (id) => {
    if (travellers.length > 1) {
      setTravellers(travellers.filter(t => t.id !== id));
    }
  };

  const updateTraveller = (id, field, value) => {
    setTravellers(travellers.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const onSubmit = async (data) => {
    // Validate travellers
    const invalidTravellers = travellers.filter(t => !t.name || !t.departureCity);
    if (invalidTravellers.length > 0) {
      toast.error('Please fill in all traveller details');
      return;
    }

    setSubmitting(true);
    try {
      const selectedApprover = approvers.find(a => a.email === data.approver);

      // Create the request
      const newRequest = await createRequest({
        type: 'travel',
        title: `Travel Request - ${data.destinationCity} - ${data.submitterName}`,
        submitterName: data.submitterName,
        destinationCity: data.destinationCity,
        purpose: data.purposeOfTravel,
        arrivalTime: data.arrivalTime.toISOString(),
        departureTime: data.departureTime.toISOString(),
        accommodationRequired: data.accommodationRequired,
        accommodationSuburb: data.accommodationSuburb || '',
        carHireRequired: data.carHireRequired,
        additionalRequirements: data.additionalRequirements || '',
        travellers: travellers,
        submittedBy: account?.email || account?.username || 'current.user@acom.edu.au',
        submittedDate: new Date().toISOString(),
        status: 'pending',
        approver: data.approver,
        approverName: selectedApprover?.name || data.approver,
      });

      // Send email notification to selected approver
      try {
        await sendApproverNotification(newRequest, data.approver);
        notifyApprovalNeeded(newRequest, data.approver);
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      // Send confirmation email to submitter
      try {
        await sendSubmitterConfirmation(newRequest);
      } catch (emailError) {
        console.error('Submitter confirmation email failed:', emailError);
      }

      toast.success('Travel request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal - Travel Request
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#388e3c' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 4, flexGrow: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#388e3c' }}>
              <Flight sx={{ mr: 1, verticalAlign: 'middle' }} />
              Travel Request
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Submit your travel requirements for approval and booking arrangements.
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Submitter Information */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Submitter Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name of Submitter"
                        {...register('submitterName')}
                        error={!!errors.submitterName}
                        helperText={errors.submitterName?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Approver"
                        {...register('approver')}
                        error={!!errors.approver}
                        helperText={errors.approver?.message || (loadingApprovers ? 'Loading approvers...' : 'Required')}
                        disabled={loadingApprovers}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            if (!selected || selected === '') {
                              return <span style={{ color: '#9e9e9e' }}>Select an approver</span>;
                            }
                            const approver = approvers.find(a => a.email === selected);
                            return approver?.name || selected;
                          }
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select an approver</em>
                        </MenuItem>
                        {approvers.map((approver) => (
                          <MenuItem key={approver.email} value={approver.email}>
                            {approver.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Travellers */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Travellers
                    </Typography>
                    <Button
                      startIcon={<Add />}
                      onClick={addTraveller}
                      variant="outlined"
                      size="small"
                    >
                      Add Traveller
                    </Button>
                  </Box>

                  <List>
                    {travellers.map((traveller, index) => (
                      <ListItem key={traveller.id} sx={{ pl: 0, pr: 0 }}>
                        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              fullWidth
                              label={`Traveller ${index + 1} Name`}
                              value={traveller.name}
                              onChange={(e) => updateTraveller(traveller.id, 'name', e.target.value)}
                              error={submitting && !traveller.name}
                              helperText={submitting && !traveller.name ? 'Required' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={5}>
                            <TextField
                              fullWidth
                              label="Departure City"
                              value={traveller.departureCity}
                              onChange={(e) => updateTraveller(traveller.id, 'departureCity', e.target.value)}
                              error={submitting && !traveller.departureCity}
                              helperText={submitting && !traveller.departureCity ? 'Required' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={2}>
                            {travellers.length > 1 && (
                              <IconButton
                                onClick={() => removeTraveller(traveller.id)}
                                color="error"
                                sx={{ mt: 1 }}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              {/* Travel Details */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Travel Details
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Destination City"
                        {...register('destinationCity')}
                        error={!!errors.destinationCity}
                        helperText={errors.destinationCity?.message}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Purpose of Travel"
                        {...register('purposeOfTravel')}
                        error={!!errors.purposeOfTravel}
                        helperText={errors.purposeOfTravel?.message}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Controller
                          name="arrivalTime"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              {...field}
                              label="Latest Time of Arrival"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.arrivalTime,
                                  helperText: errors.arrivalTime?.message,
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Controller
                          name="departureTime"
                          control={control}
                          render={({ field }) => (
                            <DateTimePicker
                              {...field}
                              label="Earliest Time of Departure"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!errors.departureTime,
                                  helperText: errors.departureTime?.message,
                                },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Accommodation and Car Hire */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    <Hotel sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Additional Requirements
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="accommodationRequired"
                          control={control}
                          render={({ field }) => (
                            <Checkbox {...field} checked={field.value} />
                          )}
                        />
                      }
                      label="Accommodation Required"
                    />
                    
                    {accommodationRequired && (
                      <TextField
                        fullWidth
                        label="Accommodation Suburb"
                        {...register('accommodationSuburb')}
                        error={!!errors.accommodationSuburb}
                        helperText={errors.accommodationSuburb?.message}
                        sx={{ ml: 4 }}
                      />
                    )}
                    
                    <FormControlLabel
                      control={
                        <Controller
                          name="carHireRequired"
                          control={control}
                          render={({ field }) => (
                            <Checkbox {...field} checked={field.value} />
                          )}
                        />
                      }
                      label="Car Hire Required"
                    />

                    <TextField
                      fullWidth
                      label="Additional Requirements"
                      multiline
                      rows={3}
                      {...register('additionalRequirements')}
                      placeholder="Enter any additional requirements or special requests..."
                      sx={{ mt: 2 }}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                  disabled={submitting || loadingApprovers}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || loadingApprovers}
                  sx={{
                    bgcolor: '#388e3c',
                    '&:hover': { bgcolor: '#2e7d32' }
                  }}
                >
                  {submitting ? 'Submitting...' : loadingApprovers ? 'Loading...' : 'Submit Travel Request'}
                </Button>
              </Box>
            </form>
          </Paper>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default TravelRequest;